import {
  HTMLAttributes,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';
import { MarketDivider } from '@market/react';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import {
  useGetOrderSummary,
  useUpdateOrderAddressInformation,
} from 'typescript/hooks/use_order_actions_client';
import { useToaster } from 'typescript/hooks/use_toaster';
import {
  ADDRESS_FIELDS,
  AddressType,
  getAddressPrefix,
} from 'typescript/schemas/address_forms/address_fields';
import useAddressForm from 'typescript/apps/checkout_v3/components/address_form/use_address_form';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { useAutoSubmit } from 'typescript/apps/checkout_v3/hooks/use_auto_submit';
import { useIsProcessingAnyRequest } from 'typescript/hooks/use_react_query_utils';
import useAddressValidation from 'typescript/hooks/use_address_validation_client';
import {
  isValidationSuccess,
  cleanAddressValidationResult,
} from 'typescript/utilities/address_validation';
import {
  ValidationRequestAddress,
  ValidationResponseAddress,
} from 'typescript/types/api/address_validation';
import { useFeatureFlags } from 'typescript/context/feature_flags';
import { CHECKOUT_ROUTES } from 'typescript/constants/enums/checkout';
import { useCompleteOrderContext } from 'typescript/context/complete_order_context';
import ShippingRatesAndAddressValidation from 'typescript/apps/checkout_v3/components/delivery/ship_to_me/shipping_rates_address_validation';
import DeliveryPageCTA from 'typescript/apps/checkout_v3/components/delivery/delivery_page_cta';
import styles from 'typescript/apps/checkout_v3/components/delivery/ship_to_me/ship_to_me.module.scss';
import {
  onCheckoutPageViewCombined,
  onCheckoutPageClick,
} from 'typescript/utilities/tracking';
import {
  COMPONENT_NAMES,
  CHECKOUT_PAGE_CLICK_EVENTS,
} from 'typescript/constants/tracking';
import { isOrderFree } from 'typescript/utilities/order_data';
import usePoBox from 'typescript/apps/checkout_v3/components/delivery/ship_to_me/use_po_box';
import RecaptchaModal from 'typescript/apps/checkout_v3/components/recaptcha/recaptcha_modal';
import { fetchLocalizedComponent } from 'typescript/utilities/component_localization';
import {
  areFormFieldsValid,
  didPrefillFieldChange,
  isCompleteRequiredShippingInformation,
} from 'typescript/utilities/address_utils';
import { captureMessage } from '@sentry/react';

const AddressFields = fetchLocalizedComponent(
  'typescript/apps/checkout_v3/components/address_form/address_fields/address_fields',
);

const ShipToMe = ({ className }: HTMLAttributes<HTMLElement>) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { countryCode } = useLocaleInfo();
  const { orderNumber, trackingData } = useCheckoutFlow();
  const { showToast } = useToaster();
  const { setShowRecaptcha } = useCompleteOrderContext();
  const { isProcessing } = useIsProcessingAnyRequest();
  const { addressValidationEnabled } = useFeatureFlags();
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });

  const [isFirstPrefill, setIsFirstPrefill] = useState(true);
  const [addressOnPrefill, setAddressOnPrefill] = useState({});

  const {
    updateOrderAddressInformation,
    isPending: isUpdateOrderAddressPending,
  } = useUpdateOrderAddressInformation({
    orderNumber,
    refetchShippingAndPaymentOptions: true,
  });
  const {
    validateAddress,
    reset: resetAddressValidation,
    data: addressValidationResult,
    isPending: isAddressValidationPending,
  } = useAddressValidation();

  const { formMethods, addressSchema } = useAddressForm({
    countryCode,
    addressType: AddressType.shipping,
  });

  const {
    control,
    formState: {
      isValid: isFormValid,
      isValidating: isFormValidating,
      isDirty: isFormDirty,
      touchedFields,
      dirtyFields,
    },
    getValues: getFormValues,
    setValue: setFormValue,
    reset: resetForm,
    watch: watchField,
    handleSubmit,
  } = formMethods;

  const { isPoBoxBlocked, showPoBoxWarning } = usePoBox({ orderNumber });

  const isAnyFieldTouched = Object.keys(touchedFields).length > 0;
  const showShippingRatesAddressValidation =
    touchedFields[ADDRESS_FIELDS.address_line_1] ||
    dirtyFields[ADDRESS_FIELDS.address_line_1] ||
    watchField(ADDRESS_FIELDS.address_line_1)?.length > 0;

  const orderHasCompleteShippingInformation =
    isCompleteRequiredShippingInformation({
      ...orderSummaryData?.shipping_address,
      ...orderSummaryData?.contact_information,
    });

  const canContinue = useMemo(() => {
    return (
      isFormValid &&
      orderHasCompleteShippingInformation &&
      !isAnyFieldTouched &&
      !isFormValidating &&
      !orderSummaryData?.bopis_fulfillment_selected &&
      !isPoBoxBlocked &&
      !isProcessing &&
      !addressValidationResult
    );
  }, [
    isFormValid,
    orderHasCompleteShippingInformation,
    isAnyFieldTouched,
    isFormValidating,
    orderSummaryData?.bopis_fulfillment_selected,
    isPoBoxBlocked,
    isProcessing,
    addressValidationResult,
  ]);

  const updateAddress = (validationAddress?: ValidationResponseAddress) => {
    // the address will be a combination of the form data and the address validation result
    // since this will be used for any update call even if it doesn't go through validation
    const address = {
      ...getFormValues(),
      ...cleanAddressValidationResult(validationAddress),
    };

    updateOrderAddressInformation(
      { address },
      {
        onError: (error) => {
          if ('missing_fields' in error && error.missing_fields) {
            showToast({
              message: t(
                'checkout.delivery.toast.missingRequiredAddressFields',
                {
                  fields: (error.missing_fields as string[])
                    .map((field) =>
                      t(`checkout.delivery.addressLabel.${field}`),
                    )
                    .join(', '),
                },
              ),
              variant: 'critical',
            });
          } else {
            showToast({
              message: t('checkout.delivery.toast.failedAddressUpdate'),
              variant: 'critical',
            });
          }
        },
        onSettled: () => {
          resetAddressValidation();
          resetForm(address);
        },
      },
    );
  };

  const handleAutoSubmit = useCallback(() => {
    // if address validation is disabled, we'll just update the address with the form data
    // this also is a workaround for cases where address validation may mistakenly correct a valid address
    // and we don't want to block the user from proceeding (we set their merchant token in the LD flag)
    if (!addressValidationEnabled) {
      updateAddress();
      return;
    }

    const formData = getFormValues();

    // react-hook-form is a little flakey with the isValid state so we need to revalidate manually
    if (!areFormFieldsValid(formData, addressSchema)) {
      // this is temp for now, we'll migrate to using datadog logs once we have it set up
      captureMessage('Schema revalidation is invalid on address autosubmit', {
        extra: {
          orderNumber,
        },
      });

      return;
    }

    // we type assert the formData to ValidationRequestAddress since we know the form fields are valid
    // and we're only sending the address fields to the address validation API
    validateAddress(formData as ValidationRequestAddress, {
      onSuccess: (validationResult) => {
        // if the address is valid, we update the order with the new address
        // if the address is partially verified or failed verification, we no op here and show the address validation component
        if (isValidationSuccess(validationResult)) {
          updateAddress(validationResult.corrected_address);
        }
      },
      onError: () => {
        // if the api call fails, we update the order address with the form data to not block the user
        updateAddress();
      },
      onSettled: () => {
        // reset the formData so it's clean for the next form change
        resetForm(formData);
      },
    });
  }, []);

  const { formRef, handleFieldFocus, handleFieldBlur } = useAutoSubmit({
    shouldAutoSubmit:
      isFormValid && isAnyFieldTouched && isFormDirty && !isFormValidating,
    onAutoSubmit: handleAutoSubmit,
  });

  const handleFieldFocusWithLogging = useCallback(
    (event) => {
      onCheckoutPageClick({
        eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.address_field_click,
        trackingData: {
          componentName: COMPONENT_NAMES.APPS.CHECKOUT_V3.SHIP_TO_ME,
          field: event.target.getAttribute('name'),
        },
      });

      handleFieldFocus();
    },
    [handleFieldFocus],
  );

  const handleFieldBlurWithLogging = useCallback(
    (event) => {
      const fieldValue = event.target.value;
      const fieldName = event.target.name;

      if (didPrefillFieldChange(addressOnPrefill, fieldName, fieldValue)) {
        onCheckoutPageClick({
          eventDesc:
            CHECKOUT_PAGE_CLICK_EVENTS.address_field_changed_from_prefill,
          trackingData: {
            componentName: COMPONENT_NAMES.APPS.CHECKOUT_V3.SHIP_TO_ME,
            field: fieldName,
          },
        });
      }

      handleFieldBlur();
    },
    [handleFieldBlur, addressOnPrefill],
  );

  const onSubmit = () => {
    if (!canContinue) {
      return;
    }

    if (isOrderFree(orderSummaryData?.payment_due)) {
      setShowRecaptcha(true);
    } else {
      history.push(CHECKOUT_ROUTES.PAYMENT);
    }
  };

  const handleSubmitWithLogging = (callback) => (event?) => {
    // Log the click event
    onCheckoutPageClick({
      eventDesc: isOrderFree(orderSummaryData?.payment_due)
        ? CHECKOUT_PAGE_CLICK_EVENTS.delivery_page_complete_order
        : CHECKOUT_PAGE_CLICK_EVENTS.delivery_page_continue_to_payment,
      trackingData,
    });

    // Blur any focused element to trigger form validation/auto-submit
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    return handleSubmit(callback)(event);
  };

  // prefill form and update form values when order summary data changes
  useEffect(() => {
    if (!isFirstPrefill || !orderSummaryData) return;

    const {
      shipping_address: address,
      contact_information: contactInformation,
    } = orderSummaryData;

    // save the address that we prefilled with so we can compare it to the form values when the user blurs a field
    // this is to see if the user has changed any of the values from the prefill
    const addressToPrefill = { ...address, ...contactInformation };
    setAddressOnPrefill(addressToPrefill);

    const prefix = getAddressPrefix(AddressType.shipping);
    Object.entries(addressToPrefill).forEach(([key, value]) => {
      setFormValue(`${prefix}${key}`, value, {
        shouldValidate: true, // validate so the isValid state reflects the actual form state
      });
    });

    // we only want this prefill to happen once on the first load of the page
    setIsFirstPrefill(false);
  }, [orderSummaryData]);

  useEffect(() => {
    onCheckoutPageViewCombined({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT_V3.SHIP_TO_ME,
      additionalParameters: trackingData,
    });
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form
        ref={formRef}
        className={`shop-grid-container ${className}`}
        onSubmit={(e) => e.preventDefault()} // prevents submit button from reloading page
        onFocus={(e) => handleFieldFocusWithLogging(e)}
        onBlur={(e) => handleFieldBlurWithLogging(e)}
      >
        <RecaptchaModal handleFormSubmit={handleSubmitWithLogging} />
        <AddressFields />
        {showShippingRatesAddressValidation && (
          <div className="shop-grid-item-full shop-grid-container no-grid-gap">
            <h3
              className={`${styles.shippingSpeedHeader} shop-grid-item-full heading-20`}
            >
              {t('checkout.delivery.title.shipping')}
            </h3>
            <ShippingRatesAndAddressValidation
              showEmptyState={
                isAddressValidationPending ||
                isUpdateOrderAddressPending ||
                (!orderHasCompleteShippingInformation &&
                  !addressValidationResult)
              }
              showPoboxBlocked={isPoBoxBlocked && isFormValid}
              addressValidationResult={addressValidationResult}
              updateAddress={updateAddress}
            />
            <MarketDivider
              className={`shop-grid-item-full ${styles.divider}`}
              size="thin"
            />
            {showPoBoxWarning && !isProcessing && !addressValidationResult && (
              <div className="shop-grid-item-full shop-grid-container no-grid-gap">
                <p className={`shop-grid-item-full ${styles.poboxWarning}`}>
                  {t('checkout.delivery.shipping.poboxWarning')}
                </p>
                <MarketDivider
                  className={`shop-grid-item-full ${styles.divider}`}
                  size="thin"
                />
              </div>
            )}
          </div>
        )}
        <DeliveryPageCTA
          className="shop-grid-item-full"
          control={control}
          // free orders will complete the order on this page so we need to make sure all the form fields are valid
          // paid orders will continue to payment, so we want to have the button to be enabled all the time
          disabled={isOrderFree(orderSummaryData?.payment_due) && !canContinue}
          isLoading={isProcessing}
          onClick={() => handleSubmitWithLogging(onSubmit)()}
        />
      </form>
    </FormProvider>
  );
};

export default ShipToMe;
